/**
 * Add organization to events API functions
 *
 * Wraps the core sys admin endpoints under /api/v2/page/event/organization, plus
 * the existing organisation GET for reloading a selected organization.
 */

import constants from '../../../constants';
import { paymentPlatformType } from './constants';
import {
  BulkAddResult,
  Organization,
  OrganizationEvent,
  OrganizationSearchResult
} from './types';

const API_BASE = `${constants.baseApiHost}/api/v2/page/event/organization`;
const ORGANIZATION_BASE = `${constants.baseApiHost}/api/v2/organization`;
const API_KEY = constants.apikey;

const getSession = () => {
  const localStorageSession = localStorage['session'];
  return localStorageSession && localStorageSession !== 'undefined'
    ? JSON.parse(localStorageSession)
    : null;
};

const getAuthHeaders = (): Record<string, string> => {
  const session = getSession();
  return {
    ApiKey: API_KEY || '',
    'Content-Type': 'application/json',
    Authorization: session?.key ? `bearer ${session.key}` : ''
  };
};

// The API wraps every payload: { data: ... }.
const handleResponse = async <T>(res: Response): Promise<T> => {
  if (!res.ok) {
    const errorText = await res.text().catch(() => res.statusText);
    throw new Error(`API ${res.status}: ${errorText}`);
  }
  const json = (await res.json()) as { data: T };
  return json.data;
};

export const searchOrganizations = async (
  text: string
): Promise<OrganizationSearchResult[]> => {
  const res = await fetch(
    `${API_BASE}/search?text=${encodeURIComponent(text)}`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  return (await handleResponse<OrganizationSearchResult[]>(res)) ?? [];
};

/** The fields of the organisation GET the organization card uses. */
interface OrganizationResponse {
  id: number;
  name: string;
  address?: { state?: string | null; country?: string | null } | null;
  paymentPlatforms?: { platformType: string }[] | null;
}

export const getOrganization = async (
  organizationId: number
): Promise<Organization> => {
  const res = await fetch(`${ORGANIZATION_BASE}/${organizationId}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  const organization = await handleResponse<OrganizationResponse | null>(res);
  if (!organization) {
    throw new Error(`Organisation ${organizationId} not found`);
  }
  return {
    id: organization.id,
    name: organization.name,
    state: organization.address?.state ?? null,
    country: organization.address?.country ?? null,
    hasPaymentPlatform: (organization.paymentPlatforms ?? []).some(
      (platform) => platform.platformType === paymentPlatformType.creditCardForm
    )
  };
};

export const getOrganizationEvents = async (
  organizationId: number
): Promise<OrganizationEvent[]> => {
  const res = await fetch(`${API_BASE}/${organizationId}/events`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  return (await handleResponse<OrganizationEvent[]>(res)) ?? [];
};

/** Adds the organization to one batch of events; the caller sends batches in turn. */
export const addOrganizationToEventsBatch = async (
  organizationId: number,
  eventIds: number[]
): Promise<BulkAddResult> => {
  const res = await fetch(`${API_BASE}/${organizationId}/events`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ eventIds })
  });
  return handleResponse<BulkAddResult>(res);
};
